import React, { memo, useEffect, useRef } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { ArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
// Registers the <math-field> web component. Nothing else on the solve page
// loads MathLive any more, so the formula field has to bring it itself.
import "mathlive";
import { a } from "../appColors";

/**
 * The row "Insert Formula" opens inside an answer box.
 *
 * A MathLive field the student writes an equation into; Enter or the arrow
 * drops it into the answer as `$$latex$$`, the same form answers have always
 * been sent to the marker in, and the row stays open for the next one.
 */
const FormulaInput = ({ isDark = false, onInsert, onClose }) => {
  const fieldRef = useRef(null);

  // Straight into the field, so the student can start typing the formula
  // they just asked for.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.focus?.();
  }, []);

  const insert = () => {
    const field = fieldRef.current;
    const latex = field?.value?.trim();
    if (!latex) return;
    onInsert?.(latex);
    field.value = "";
    field.focus?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      insert();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  return (
    <Flex
      align="center"
      gap="8px"
      mx="19px"
      mb="4px"
      px="10px"
      py="6px"
      border="1px solid"
      borderColor={a.edge}
      borderRadius="10px"
      bg={a.surfaceSubtle}
    >
      <Box flex="1" minW={0}>
        <math-field
          ref={fieldRef}
          onKeyDown={onKeyDown}
          aria-label="Formula"
          style={{
            display: "block",
            width: "100%",
            fontSize: "16px",
            border: "none",
            background: "transparent",
            color: isDark ? "#f5f3f7" : "#000000",
            "--keyboard-background-color": isDark ? "#191619" : "#ffffff",
          }}
        />
      </Box>

      <Flex
        as="button"
        type="button"
        aria-label="Insert formula into answer"
        title="Insert formula (Enter)"
        align="center"
        justify="center"
        boxSize="26px"
        flexShrink={0}
        borderRadius="full"
        border="1px solid"
        borderColor={a.text}
        color={a.text}
        cursor="pointer"
        _hover={{ bg: a.text, color: a.surface }}
        onClick={insert}
      >
        <ArrowUpIcon style={{ width: 14, height: 14 }} />
      </Flex>

      <Box
        as="button"
        type="button"
        aria-label="Close formula input"
        title="Close (Esc)"
        lineHeight="0"
        flexShrink={0}
        color={a.textSub}
        cursor="pointer"
        _hover={{ color: a.text }}
        onClick={onClose}
      >
        <XMarkIcon style={{ width: 16, height: 16 }} />
      </Box>
    </Flex>
  );
};

export default memo(FormulaInput);
